import { Dtos, NoteTopicId, Relations } from "@inrev/common";
import { useGlobalErrorMessageModal } from "@portal/providers/GlobalErrorHandlingProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRequest } from "../../../utils/request";
import { adminNoteTopicsQueryKey } from "../note.keys";
import { AdminNoteTopic } from "./types";

export const useUpdateAdminNote = () => {
	const queryClient = useQueryClient();
	const { patch } = useRequest();
	const { triggerErrorModal } = useGlobalErrorMessageModal();

	const mutation = useMutation({
		mutationFn: async ({
			data,
			noteId,
			topicId,
		}: {
			data: Dtos.Admin.AdminNote.Update.Request;
			noteId: string;
			topicId: NoteTopicId;
			relation: Relations.Notes;
		}) => {
			return await patch<Dtos.Admin.AdminNote.Update.Response>(
				`/v2/admin/admin-notes/note-topics/${topicId}/notes/${noteId}`,
				data,
			);
		},
		onMutate: async ({ data, noteId, topicId, relation }) => {
			await queryClient.cancelQueries({
				queryKey: adminNoteTopicsQueryKey(relation),
			});
			const previousTopics = queryClient.getQueryData<AdminNoteTopic[]>(
				adminNoteTopicsQueryKey(relation),
			);
			queryClient.setQueryData(
				adminNoteTopicsQueryKey(relation),
				(oldTopics: AdminNoteTopic[] | undefined) => {
					if (!oldTopics) return [];
					return oldTopics.map((topic) => {
						if (topic.id === topicId) {
							return {
								...topic,
								notes: topic.notes.map((note) => {
									if (note.id === noteId) {
										return {
											...note,
											...data,
										};
									}
									return note;
								}),
							};
						}
						return topic;
					});
				},
			);
			return { previousTopics, relation };
		},
		onError: (error: Error, _inputData, context) => {
			if (context?.previousTopics !== undefined) {
				queryClient.setQueryData(adminNoteTopicsQueryKey(context.relation), context.previousTopics);
			}
			triggerErrorModal(error);
		},
	});

	return {
		updateAdminNote: mutation.mutate,
		updateAdminNoteIsLoading: mutation.isPending,
	};
};
